import React from "react";
import axios from "axios";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { adminNextFromSearch } from "utils/adminNext";
import {
  Button,
  Container,
  CssBaseline,
  Divider,
  TextField,
  Box,
  Link,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { makeStyles, withStyles, useTheme } from "@material-ui/core/styles";
import { UserContext } from "contexts/UserContext";
import type { AppConfig } from "models/config"
import GoogleLoginButton from 'react-social-login-buttons/lib/buttons/GoogleLoginButton';
import { LoginSocialGoogle } from 'reactjs-social-login';
import MicrosoftLoginButton from 'react-social-login-buttons/lib/buttons/MicrosoftLoginButton';
import { startMicrosoftSignIn, hasPendingMicrosoftSignIn, takeMicrosoftSignIn } from "utils/microsoftSignIn";
import { fixGoogleUserinfoProxy } from "utils/fixGoogleUserinfoProxy";
import { buildUser } from "utils/user";
import logo from "assets/logo.png";

fixGoogleUserinfoProxy();

const PrivacyPolicy = () => (
  <Typography variant="body2">
    <Link
      color="textSecondary"
      href="/privacy_policy"
    >
      Privacy Policy
    </Link>
    {" · "}
    <Link
      color="textSecondary"
      href="/terms_of_service"
    >
      Terms of Service
    </Link>
  </Typography>
);

const CssTextField = withStyles((theme) => ({
  root: {
    "& label.Mui-focused": {
      color: theme.palette.text.secondary,
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: theme.palette.text.secondary,
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: theme.palette.text.secondary,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.text.secondary,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.text.secondary,
      },
    },
  },
}))(TextField);

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4, 2),
  },
  content: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  logo: {
    height: 72,
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      height: 56,
    },
  },
  spaceAfter: {
    marginBottom: 15
  },
  // Three-column split (SSO | divider | form). Using CSS grid rather than
  // flexbox for this: grid rows stretch every cell to the same height by
  // default, so the divider column reliably matches whichever of its
  // siblings is taller - no percentage-height flexbox tricks needed.
  splitGrid: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "1fr",
    gridRowGap: theme.spacing(3),
    marginTop: theme.spacing(2),
    [theme.breakpoints.up("md")]: {
      gridTemplateColumns: "1fr auto 1fr",
      gridColumnGap: theme.spacing(4),
    },
  },
  ssoColumn: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  formColumn: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  dividerColumn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.up("md")]: {
      flexDirection: "column",
    },
  },
  divider: {
    flexGrow: 1,
  },
  orLabel: {
    margin: theme.spacing(0, 2),
    [theme.breakpoints.up("md")]: {
      margin: theme.spacing(2, 0),
    },
  },
  form: {
    width: "100%", // Fix IE 11 issue.
  },
  submit: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
    backgroundColor: "#dcd8d8",
  },
  forgotPassword: {
    width: "100%",
    textAlign: "right",
  },
  link: {
    color: theme.palette.text.secondary,
  },
  copyrights: {
    marginTop: theme.spacing(5),
  },
  socialButton: {
    marginBottom: "25px",
    width: '100%'
  },
}));

type Props = {
  config: AppConfig
}

export const Login = (props: Props) => {
  const { config } = props;
  const googleAppId = config ? config.socialApps.google : null;
  const microsoftAppId = config ? config.socialApps.microsoft : null;

  const userContext = React.useContext(UserContext);
  const history = useHistory();
  const location = useLocation();
  // Set by PrivateRoute when it redirected an unauthenticated visit
  // here - send the user back to whatever page they originally asked
  // for instead of always dropping them on the landing page.
  const from = (location.state && location.state.from) || "/song-proposals";
  // Set by the Django admin (WT/urls.py) when a signed-out visitor opens it:
  // after signing in here they are sent back to the admin, which needs a full
  // page load because it is not part of this app.
  const adminNext = adminNextFromSearch(location.search);
  const goToAdmin = () => {
    if (adminNext) window.location.assign(adminNext);
  };
  // Set by SocialLinkConfirm (api/views.py) redirecting back here after
  // the emailed confirmation link is clicked - see VerifyToLink and
  // UserSocialAccountAdapter.pre_social_login (song_proposals/adapters.py).
  // A one-time message: read once on mount, not tied to location.search
  // staying around, so it doesn't linger through an unrelated later
  // failed login attempt on this same page load.
  const [linkParam] = React.useState(
    () => new URLSearchParams(location.search).get("linked")
  );
  const [linkErrorParam] = React.useState(
    () => new URLSearchParams(location.search).get("link_error")
  );
  const classes = useStyles();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const hasSocialLogin = Boolean(googleAppId || microsoftAppId);

  const [formData, setFormData] = React.useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = React.useState({});
  const [mode, setMode] = React.useState("login"); // modes: login, reset_password, text
  // Google/Microsoft's own SDKs reject silently in a few real-world
  // cases (the SDK script hasn't finished loading yet, a popup blocked
  // by the browser, third-party cookies blocked) - Safari in particular
  // hits these far more often than Chrome. Without this, the only trace
  // was a console.log() nobody but a developer would ever see - from a
  // user's seat, the button just did nothing.
  const [ssoError, setSsoError] = React.useState("");
  const handleChange = (event) => {
    if (event.target.name === "email") {
      if ("email" in errors) {
        setErrors({});
      }
    }
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  // DRF's error shape for a {code, detail} dict raised straight from a
  // view/adapter (the social-login path below) stays a flat string -
  // there's no equivalent here for the plain email/password path,
  // which deliberately never distinguishes "no such email" from "wrong
  // password" in its response (see CustomLoginSerializer.validate()).
  const errorCode = (data) => {
    const code = data && data.code;
    return Array.isArray(code) ? code[0] : code;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (mode === "login") {
      axios
        .post("/auth/login/", formData)
        .then((res) => {
          const user = buildUser(res.data.user, true);
          localStorage.setItem("token", res.data.access);
          localStorage.setItem("user", JSON.stringify(user));
          userContext.setUser(user);
          goToAdmin();
        })
        .catch((err) => {
          if (err && err.response && err.response.data && err.response.data) {
            setErrors(err.response.data);
          }
        });
    } else {
      axios
        .post("/auth/password/reset/", { email: formData.email })
        .then((res) => {
          setMode("text");
        })
        .catch((err) => {
          if (err && err.response && err.response.data && err.response.data) {
            setErrors(err.response.data);
            console.log(err.response.data);
          }
        });
    }
  };

  const responseSocial = (provider, accessToken, email) => {
      axios
        .post(`/auth/${provider}/`, {
          access_token: accessToken,
        })
        .then((res) => {
          const user = buildUser(res.data.user, true);
          localStorage.setItem("token", res.data.access);
          localStorage.setItem("user", JSON.stringify(user));
          userContext.setUser(user);
          goToAdmin();
        })
        .catch((err) => {
          const code = err && err.response && err.response.data && errorCode(err.response.data);
          if (code === "not_invited") {
            history.push("/not-invited", { email });
            return;
          }
          if (code === "email_in_use") {
            history.push("/email-in-use", { email });
            return;
          }
          if (code === "verify_to_link") {
            history.push("/verify-to-link", { email });
            return;
          }
          if (err && err.response && err.response.data && err.response.data) {
            setErrors(err.response.data);
          }
        });
  };

  const responseGoogle = (res) => {
    if (res && res.access_token) {
      responseSocial('google', res.access_token, res.email)
    }
  };

  const responseMicrosoft = (res) => {
    if (res && res.access_token) {
      responseSocial('microsoft', res.access_token, res.mail || res.userPrincipalName)
    }
  };

  // Back from Microsoft's sign-in (utils/microsoftSignIn.js): finish it.
  React.useEffect(() => {
    if (!microsoftAppId || !hasPendingMicrosoftSignIn()) return;
    takeMicrosoftSignIn(microsoftAppId).then(responseMicrosoft).catch(handleSsoReject("Microsoft"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [microsoftAppId]);

  const handleSsoReject = (providerLabel) => (err) => {
    console.log(err);
    setSsoError(
      `Couldn't sign in with ${providerLabel} - try again, or use email below.`
    );
  };

  // Covers both a submit that just succeeded in *this* tab (the success
  // handlers above set userContext.user directly - no separate local
  // "logged in" flag needed: adding one and setting it in the same
  // .then() would trigger this same redirect a second time, one tick
  // after userContext.setUser() already unmounted this component via
  // the redirect below, logging a "state update on an unmounted
  // component" warning) and someone already authenticated (logged in from
  // another tab, or a still-valid session from before) landing directly
  // on /login (a stale bookmark, browser back, or a refresh) - bounces
  // straight to `from` instead of showing the form. Safe to read
  // userContext.user here with no extra loading check: App.js only
  // renders this route at all once its own auth verification
  // (authChecked) has settled.
  // With an admin destination the form stays up even if the app already
  // considers you signed in: the admin needs its own session, which only a
  // fresh sign-in here creates (bouncing straight back could loop).
  if (userContext.user.isAuthenticated && !adminNext) {
    return <Redirect to={from} />;
  }

  return (
    <Box className={classes.root} component="main">
      <CssBaseline />
      <Container maxWidth="md">
        <div className={classes.content}>
          <a href="/">
            <img src={logo} alt="PlanPraise" className={classes.logo} />
          </a>
          <Typography component="h1" variant="h5">
            <span className={classes.spaceAfter}>
              {mode === "login" ? "Sign in to PlanPraise" : "Reset password"}
            </span>
          </Typography>

          {"non_field_errors" in errors && (
            <Typography
              variant="caption"
              color="error"
              display="block"
              align="center"
            >
              <span className={classes.spaceAfter}>{errors.non_field_errors}</span>
            </Typography>
          )}

          {linkParam === "1" && (
            <Typography variant="caption" color="secondary" display="block" align="center">
              <span className={classes.spaceAfter}>
                Sign-in method connected - sign in below to continue.
              </span>
            </Typography>
          )}
          {linkErrorParam && (
            <Typography variant="caption" color="error" display="block" align="center">
              <span className={classes.spaceAfter}>
                {linkErrorParam === "expired"
                  ? "That confirmation link has expired - try signing in again to get a new one."
                  : "That confirmation link isn't valid - try signing in again to get a new one."}
              </span>
            </Typography>
          )}

          {mode === "login" ? (
            hasSocialLogin ? (
              <div className={classes.splitGrid}>
                <div className={classes.ssoColumn}>
                  {ssoError && (
                    <Typography
                      variant="caption"
                      color="error"
                      display="block"
                      align="center"
                    >
                      <span className={classes.spaceAfter}>{ssoError}</span>
                    </Typography>
                  )}
                  {googleAppId && (
                    <LoginSocialGoogle
                        className={classes.socialButton}
                        client_id={googleAppId}
                        scope="openid profile email"
                        discoveryDocs="claims_supported"
                        access_type="offline"
                        onResolve={({ provider, data }) => responseGoogle(data)}
                        onReject={handleSsoReject("Google")}>
                      <GoogleLoginButton text={"SIGN IN WITH GOOGLE"}/>
                    </LoginSocialGoogle>
                  )}
                  {microsoftAppId && (
                    <div className={classes.socialButton}>
                      <MicrosoftLoginButton
                        onClick={() =>
                          startMicrosoftSignIn({ clientId: microsoftAppId }).catch(handleSsoReject("Microsoft"))
                        }
                      />
                    </div>
                  )}
                </div>
                <div className={classes.dividerColumn}>
                  <Divider
                    className={classes.divider}
                    orientation={isDesktop ? "vertical" : "horizontal"}
                    flexItem
                  />
                  <Typography variant="body2" color="textSecondary" className={classes.orLabel}>
                    OR
                  </Typography>
                  <Divider
                    className={classes.divider}
                    orientation={isDesktop ? "vertical" : "horizontal"}
                    flexItem
                  />
                </div>
                <div className={classes.formColumn}>
                  <form className={classes.form} onSubmit={handleSubmit}>
                    <CssTextField
                      variant="outlined"
                      margin="normal"
                      fullWidth
                      id="email"
                      label="Email Address"
                      name="email"
                      autoComplete="email"
                      type="email"
                      onChange={handleChange}
                      error={"email" in errors}
                      helperText={"email" in errors && errors.email}
                    />
                    <CssTextField
                      variant="outlined"
                      margin="normal"
                      fullWidth
                      name="password"
                      label="Password"
                      type="password"
                      id="password"
                      autoComplete="current-password"
                      onChange={handleChange}
                    />
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      className={classes.submit}
                    >
                      Sign In
                    </Button>
                  </form>
                  <Box className={classes.forgotPassword}>
                    <Button size="small" onClick={() => setMode("reset_password")}>
                      <Typography variant="caption">Forgot password?</Typography>
                    </Button>
                  </Box>
                </div>
              </div>
            ) : (
              <>
                <form className={classes.form} onSubmit={handleSubmit}>
                  <CssTextField
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    id="email"
                    label="Email Address"
                    name="email"
                    autoComplete="email"
                    type="email"
                    autoFocus
                    onChange={handleChange}
                    error={"email" in errors}
                    helperText={"email" in errors && errors.email}
                  />
                  <CssTextField
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    name="password"
                    label="Password"
                    type="password"
                    id="password"
                    autoComplete="current-password"
                    onChange={handleChange}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    className={classes.submit}
                  >
                    Sign In
                  </Button>
                </form>
                <Box style={{ width: "100%" }}>
                  <Button size="small" onClick={() => setMode("reset_password")}>
                    <Typography variant="caption">Forgot password?</Typography>
                  </Button>
                </Box>
              </>
            )
          ) : mode === "reset_password" ? (
            <>
              <form className={classes.form} onSubmit={handleSubmit}>
                <CssTextField
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  type="email"
                  autoFocus
                  onChange={handleChange}
                  error={"email" in errors}
                  helperText={"email" in errors && errors.email}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  className={classes.submit}
                >
                  Reset password
                </Button>
              </form>
              <Box mt={2} style={{ width: "100%" }}>
                <Button size="small" onClick={() => setMode("login")}>
                  <Typography variant="caption">Back to login</Typography>
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Box mt={6}>
                <Typography>
                  Please follow the instructions sent to {formData.email} to reset
                  your password.
                </Typography>
              </Box>
              <Box mt={4} align="center" style={{ width: "100%" }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setFormData({ email: "", password: "" });
                    setMode("login");
                  }}
                >
                  <Typography variant="caption">Back to login</Typography>
                </Button>
              </Box>
            </>
          )}
          <Box mt={3}>
            <Typography variant="body2" align="center">
              <Link color="textSecondary" href="/request-access">
                New team? Request access
              </Link>
            </Typography>
          </Box>
          <Box mt={5} className={classes.copyrights}>
            <PrivacyPolicy />
          </Box>
        </div>
      </Container>
    </Box>
  );
};